"use client";

import { type ReactNode, useEffect } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { JobRow, type JobRowProps } from "./job-row";
import { ZeroResultAlert } from "./zero-result-alert";

/**
 * Fallback chips when the query maps to no category: broad category pages
 * that always carry inventory. Never other zero-result queries — a chip that
 * lands on another empty page is a dead end (the 30-day top zero-result
 * queries, `paralegal` / `seamtress`, are listed in the PR body).
 */
const FALLBACK_CATEGORY_SLUGS = ["engineering", "marketing"];

/**
 * Coarse, whole-word keyword → category-page match for a free-text search
 * query. Deliberately conservative (word-boundary regex, not substring) so
 * "email" never matches the "ai" keyword — a false chip is worse than no
 * chip. Returns null when nothing matches; the caller falls back to the two
 * global queries only.
 */
const CATEGORY_KEYWORDS: Array<{
	slug: string;
	label: string;
	pattern: RegExp;
}> = [
	{
		slug: "engineering",
		label: "Engineering",
		pattern: /\b(engineer|developer|programmer|software)\b/i,
	},
	{ slug: "design", label: "Design", pattern: /\b(design|ux|ui)\b/i },
	{
		slug: "marketing",
		label: "Marketing",
		pattern: /\b(marketing|growth)\b/i,
	},
	{
		slug: "data-analytics",
		label: "Data & Analytics",
		pattern: /\b(data|analytics|analyst)\b/i,
	},
	{ slug: "ai", label: "AI", pattern: /\b(ai|machine learning|ml)\b/i },
	{
		slug: "sales",
		label: "Sales & Biz Dev",
		pattern: /\b(sales|business development)\b/i,
	},
];

function deriveZeroResultChips(query: string, locale?: string) {
	const loc = locale ?? "en";
	const category = CATEGORY_KEYWORDS.find((c) => c.pattern.test(query));
	const chips: Array<{ label: string; href: string }> = [];
	if (category) {
		chips.push({
			label: category.label,
			href: `/${loc}/remote-jobs/${category.slug}#feed`,
		});
	}
	for (const slug of FALLBACK_CATEGORY_SLUGS) {
		if (chips.length >= 3) break;
		const c = CATEGORY_KEYWORDS.find((k) => k.slug === slug);
		if (!c || c.slug === category?.slug) continue;
		chips.push({
			label: c.label,
			href: `/${loc}/remote-jobs/${c.slug}#feed`,
		});
	}
	return chips;
}

/**
 * Pure (non-async, no server-only imports) row-list renderer shared by
 * `JobFeed` (SSR — awaits `attachCompanyDomains` then delegates here) and the
 * client-side feed island (`<PersonalizedFeed>`, PR-4), which receives
 * already-enriched jobs straight from `/api/feed`'s JSON response. Kept in
 * its OWN file (not job-feed.tsx) so a client component can import it without
 * dragging `attachCompanyDomains`'s server-only D1 imports into the client
 * bundle — `job-feed.tsx` stays an async server component, this file has no
 * async/server-only code at all.
 */
export type JobFeedRowsProps = {
	/**
	 * REQUIRED (not optional) `companyDomain`/`companyLogoR2Key` — deliberately
	 * stricter than `JobRowProps["job"]`'s own optional declaration of the same
	 * fields, which TypeScript's intersection rules make binding here: a caller
	 * that forgets to run raw feed rows through `attachCompanyDomains` (the
	 * 2026-07 island bug — every row silently rendered the monogram fallback
	 * even when the DB had a resolved logo) now fails `tsc` instead of
	 * silently degrading in production.
	 */
	jobs: Array<
		JobRowProps["job"] & {
			companyDomain: string | null;
			companyLogoR2Key: string | null;
			companyLogoConfidence?: number | null;
		}
	>;
	startIndex?: number;
	/** Forwarded to every row as its `list_surface` analytics prop. */
	listSurface?: string;
	/** Page-level ranking variant forwarded to every row — see
	 * `JobRowProps.algoVersion`. */
	algoVersion?: string;
	isAuthenticated?: boolean;
	isPreview?: boolean;
	t?: Dictionary;
	locale?: string;
	/** Whether the viewer's plan allows saving jobs — forwarded to each row's
	 * SaveJobButton. Omit for guests (SaveJobButton redirects them to login). */
	canSaveJobs?: boolean;
	/** Viewer's saved job IDs — forwarded to each row so the bookmark reflects
	 * the viewer's state. Omit for guests. */
	savedJobIds?: Set<string>;
	/**
	 * Optional inline node (e.g. <FeedNudgeCard />) injected after the Nth
	 * row. If `inlineNudgeAfter` is not provided or the feed has fewer rows
	 * than that, the node is not rendered.
	 */
	inlineNudge?: ReactNode;
	inlineNudgeAfter?: number;
	/**
	 * Optional inline ad node (the `feed_inline` monetization surface), injected
	 * after the Nth row — independent of the nudge so the two can coexist at
	 * different positions. Same "render nothing if the feed is too short" rule.
	 *
	 * `inlineAdAfter` accepts a single position OR an array of positions for
	 * multiple in-feed units. Positions past the end of the feed are skipped.
	 */
	inlineAd?: ReactNode;
	inlineAdAfter?: number | number[];
	/** One display AdSlot rendered after this 1-based row (e.g. 12). Separate
	 *  from `inlineAd` so the affiliate card (row 8) and the display unit never
	 *  share a position. */
	inlineDisplay?: ReactNode;
	inlineDisplayAfter?: number;
	/** Forwarded to every row — see the doc comment on `JobRowProps.currentPath`
	 * in job-row.tsx (prerender-safety: replaces the row's old `usePathname()`
	 * call). */
	currentPath?: string;
	/** Forwarded to every row — see the doc comment on `JobRowProps.searchQuery`
	 * in job-row.tsx (prerender-safety: replaces the row's old
	 * `useSearchParams()` call). */
	searchQuery?: string | null;
};

export function JobFeedRows({
	jobs: enriched,
	startIndex = 1,
	listSurface,
	algoVersion,
	isAuthenticated = false,
	isPreview = false,
	t,
	locale,
	canSaveJobs,
	savedJobIds,
	inlineNudge,
	inlineNudgeAfter,
	inlineAd,
	inlineAdAfter,
	inlineDisplay,
	inlineDisplayAfter,
	currentPath,
	searchQuery,
}: JobFeedRowsProps) {
	const { trackGateImpression } = useAnalytics();
	useEffect(() => {
		const lockedCardCount =
			enriched?.filter((job) => isPreview || job.isPreview).length ?? 0;
		if (lockedCardCount === 0) return;
		trackGateImpression({
			surface_id: "feed_card_locked",
			gate_type: isAuthenticated ? "upgrade" : "signup",
			trigger: "view_apply",
			locked_card_count: lockedCardCount,
		});
		// `enriched`'s reference (not its length) is the dependency deliberately —
		// a new array means a new feed render (page/filter/fetch), which is the
		// "once per gate-view" granularity this event needs (see job-row.tsx).
	}, [enriched, isPreview, isAuthenticated, trackGateImpression]);

	if (!enriched || enriched.length === 0) {
		const trimmedQuery = searchQuery?.trim();
		if (trimmedQuery) {
			return (
				<div className="py-10">
					<ZeroResultAlert
						locale={locale ?? "en"}
						query={trimmedQuery}
						chips={deriveZeroResultChips(trimmedQuery, locale)}
						t={t}
					/>
				</div>
			);
		}
		const suggestions = [
			{ label: "Engineering", href: `/${locale}/remote-jobs/engineering#feed` },
			{ label: "Design", href: `/${locale}/remote-jobs/design#feed` },
			{ label: "Marketing", href: `/${locale}/remote-jobs/marketing#feed` },
			{
				label: "Data & Analytics",
				href: `/${locale}/remote-jobs/data-analytics#feed`,
			},
			{ label: "AI", href: `/${locale}/remote-jobs/ai#feed` },
			{ label: "Sales & Biz Dev", href: `/${locale}/remote-jobs/sales#feed` },
		];
		return (
			<div className="py-14 text-center">
				<p className="text-base font-semibold text-foreground">
					{t?.noJobsFound || "No jobs found"}
				</p>
				<p className="mt-1 text-sm text-muted-foreground">
					{t?.adjustSearch || "Try a different keyword, or browse by category:"}
				</p>
				<div className="mt-4 flex flex-wrap justify-center gap-2">
					{suggestions.map((s) => (
						<a
							key={s.href}
							href={s.href}
							className="rounded-full border border-border/60 bg-card/70 min-h-11 px-3.5 py-1.5 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
						>
							{s.label}
						</a>
					))}
				</div>
				<a
					href={`/${locale}/jobs#feed`}
					className="mt-5 inline-block text-sm text-primary underline-offset-4 hover:underline"
				>
					{t?.clearSearch || "Clear search and show all jobs"}
				</a>
			</div>
		);
	}

	const shouldInjectNudge =
		inlineNudge != null &&
		typeof inlineNudgeAfter === "number" &&
		inlineNudgeAfter > 0 &&
		enriched.length > inlineNudgeAfter;

	const adPositions = new Set(
		inlineAd != null
			? (Array.isArray(inlineAdAfter) ? inlineAdAfter : [inlineAdAfter]).filter(
					(n): n is number =>
						typeof n === "number" && n > 0 && enriched.length > n,
				)
			: [],
	);

	const shouldInjectDisplay =
		inlineDisplay != null &&
		typeof inlineDisplayAfter === "number" &&
		inlineDisplayAfter > 0 &&
		enriched.length > inlineDisplayAfter;

	return (
		<div className="flex flex-col">
			{enriched.flatMap((job, idx) => {
				const row = (
					<JobRow
						key={job.id}
						job={job}
						index={startIndex + idx}
						listSurface={listSurface}
						algoVersion={algoVersion}
						isAuthenticated={isAuthenticated}
						isPreview={isPreview || job.isPreview}
						t={t}
						locale={locale}
						canSaveJobs={canSaveJobs}
						savedJobIds={savedJobIds}
						currentPath={currentPath}
						searchQuery={searchQuery}
					/>
				);
				const out: ReactNode[] = [row];
				if (adPositions.has(idx + 1)) {
					// biome-ignore lint/suspicious/noArrayIndexKey: the feed position is a stable slot identity (rows never reorder client-side), not a volatile list index.
					out.push(<div key={`__inline_ad_${idx + 1}`}>{inlineAd}</div>);
				}
				if (shouldInjectDisplay && idx + 1 === inlineDisplayAfter) {
					out.push(
						// biome-ignore lint/suspicious/noArrayIndexKey: the feed position is a stable slot identity (rows never reorder client-side), not a volatile list index.
						<div key={`__inline_display_${idx + 1}`}>{inlineDisplay}</div>,
					);
				}
				if (shouldInjectNudge && idx + 1 === inlineNudgeAfter) {
					out.push(<div key="__inline_nudge">{inlineNudge}</div>);
				}
				return out;
			})}
		</div>
	);
}
